import React, { useState, useEffect, useRef } from 'react';
import { damageCatalogApi, formatApiError } from '../services/api';
import { MagnifyingGlass, X, ListBullets } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { isTotalLossItem } from '../lib/damageBilling';

const CATEGORY_LABELS = {
  lack: 'Lack', karosserie: 'Karosserie', glas: 'Glas', reifen: 'Reifen',
  innenraum: 'Innenraum', technik: 'Technik', reinigung: 'Reinigung', sonstige: 'Sonstige',
};

const formatEur = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v));

/**
 * DamageCatalogPicker
 *  - Combobox with classic dropdown + live search
 *  - onSelect(item) → caller pre-fills the damage form
 *
 * Props:
 *  - onSelect(item)
 */
const DamageCatalogPicker = ({ onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    damageCatalogApi.getAll({ only_active: true })
      .then((res) => { if (active) setItems(res.data || []); })
      .catch((err) => { if (active) toast.error(formatApiError(err) || 'Schadenskatalog konnte nicht geladen werden'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((i) =>
        (i.name || '').toLowerCase().includes(q) ||
        (CATEGORY_LABELS[i.category] || '').toLowerCase().includes(q) ||
        (i.default_description || '').toLowerCase().includes(q)
      )
    : items;

  const pick = (item) => {
    onSelect?.(item);
    setOpen(false);
    setQuery('');
    toast.success(`„${item.name}" übernommen`);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="damage-catalog-picker">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(!open);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className="gap-2"
        data-testid="damage-catalog-open-btn"
      >
        <ListBullets size={14} />
        Aus Katalog wählen…
      </Button>

      {open && (
        <div className="absolute z-50 mt-2 left-0 w-[420px] max-w-[90vw] bg-white border border-[#E2E4E9] rounded-sm shadow-lg" data-testid="damage-catalog-dropdown">
          <div className="p-2 border-b border-[#E2E4E9]">
            <div className="relative">
              <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A94A6]" size={14} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suchen (Name, Kategorie, Beschreibung)…"
                className="w-full pl-8 pr-7 py-1.5 text-sm border border-[#E2E4E9] rounded-sm focus:outline-none focus:border-[#E60000]"
                data-testid="damage-catalog-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A94A6] hover:text-[#111418]"
                  aria-label="Suche leeren"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {loading ? (
              <p className="text-sm text-[#8A94A6] px-3 py-3">Lade Katalog…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-[#8A94A6] px-3 py-3">
                {q ? `Keine Treffer für „${query}"` : 'Katalog ist leer.'}
              </p>
            ) : (
              filtered.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => pick(item)}
                  className="w-full text-left px-3 py-2 hover:bg-[#FAFAFB] border-b border-[#F0F1F4] last:border-b-0 group"
                  data-testid={`damage-catalog-item-${item.id}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-semibold text-[#111418] group-hover:text-[#E60000] transition-colors">
                      {item.name}
                    </span>
                    <span className="text-xs font-bold text-[#E60000] tabular-nums">
                      {isTotalLossItem(item) ? 'SB laut Buchung' : formatEur(item.default_cost)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-[#525866]">
                    <span className="font-medium uppercase tracking-wide bg-[#F1F2F4] px-1.5 py-0.5 rounded">
                      {CATEGORY_LABELS[item.category] || item.category}
                    </span>
                    {item.default_description && (
                      <span className="truncate">{item.default_description}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DamageCatalogPicker;
